import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, put } from "./api";
import { idbDel, idbGet, idbSet } from "./idb";
import { useUi } from "./store";
import { offlineCopiesEnabled } from "@/features/notes/collab";
import type { FolderRow, Member, NoteDetail, NoteRow, User, Workspace } from "./types";

export const qk = {
  config: ["config"] as const,
  me: ["me"] as const,
  workspaces: ["workspaces"] as const,
  workspace: (id: string) => ["workspace", id] as const,
  tree: (id: string) => ["tree", id] as const,
  note: (id: string) => ["note", id] as const,
  prefs: (scope: string) => ["prefs", scope] as const,
  users: ["users"] as const,
  tasks: (scope: string) => ["tasks", scope] as const,
  library: (ws: string) => ["library", ws] as const,
  manuscripts: (ws: string) => ["manuscripts", ws] as const,
};

export function useConfig() {
  return useQuery({ queryKey: qk.config, queryFn: () => get<{ appName: string; devIdentity: boolean; identityKind: string; environment: string; maxUploadMb: number; aiIncluded?: boolean; mailConfigured?: boolean; paymentsOpen?: boolean }>("/api/config"), staleTime: Infinity });
}

export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return (await get<{ user: User }>("/api/me")).user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
  });
}

export function useWorkspaces() {
  return useQuery({ queryKey: qk.workspaces, queryFn: async () => (await get<{ workspaces: Workspace[] }>("/api/workspaces")).workspaces });
}

export function useWorkspace(id: string | undefined) {
  return useQuery({
    queryKey: qk.workspace(id ?? ""),
    queryFn: () => get<{ workspace: Workspace & { createdAt: string }; members: Member[] }>(`/api/workspaces/${id}`),
    enabled: !!id,
  });
}

export function useTree(id: string | undefined) {
  return useQuery({
    queryKey: qk.tree(id ?? ""),
    queryFn: () => get<{ folders: FolderRow[]; notes: NoteRow[] }>(`/api/workspaces/${id}/tree`),
    enabled: !!id,
  });
}

export function useNote(id: string | undefined) {
  const userId = useUi((s) => s.userId);
  return useQuery({
    queryKey: qk.note(id ?? ""),
    queryFn: async () => {
      try {
        const d = await get<NoteDetail>(`/api/notes/${id}`);
        // Metadata for opening the note offline; the body is cached separately (ciphertext for encrypted notes).
        if (userId && offlineCopiesEnabled()) void idbSet(userId, `note:${id}`, { ...d, note: { ...d.note, contentMd: "" } });
        return d;
      } catch (e) {
        if (e instanceof ApiError && e.status === 0 && userId) {
          const cached = await idbGet<NoteDetail>(userId, `note:${id}`);
          if (cached) return { ...cached, offline: true };
        }
        if (e instanceof ApiError && [403, 404].includes(e.status) && userId) void idbDel(userId, `note:${id}`);
        throw e;
      }
    },
    enabled: !!id,
    retry: false,
  });
}

export function useUsers() {
  return useQuery({ queryKey: qk.users, queryFn: async () => (await get<{ users: { id: string; displayName: string }[] }>("/api/users")).users });
}

export function usePrefs(scope: string | undefined) {
  return useQuery({
    queryKey: qk.prefs(scope ?? "global"),
    queryFn: async () => (await get<{ prefs: Record<string, any> }>(`/api/prefs?scope=${encodeURIComponent(scope ?? "global")}`)).prefs,
    enabled: !!scope,
  });
}

export function useSetPref(scope: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => put("/api/prefs", { scope: scope ?? "global", key, value }),
    onMutate: async ({ key, value }) => {
      const k = qk.prefs(scope ?? "global");
      qc.setQueryData(k, (old: Record<string, any> | undefined) => ({ ...(old ?? {}), [key]: value }));
    },
  });
}

export function memberName(members: Member[] | undefined, id: string | null | undefined) {
  if (!id) return null;
  return members?.find((m) => m.id === id)?.displayName ?? "Former member";
}
